// The BusinessRule.Json column holds RulesEngine-style rulesets in a few shapes:
// an array of workflows ([{ WorkflowName, Rules }]), an object with a Rules array,
// or a single rule object. Keys are PascalCase in the database but are accessed
// case-insensitively here so odd rows still parse. Rule objects self-reference
// child rules through their Rules property, which forms the hierarchy.
// Parsed objects are kept by reference so untouched properties round-trip unchanged.

export const findKey = (obj, key) => {
    if (!obj || typeof obj !== 'object') return undefined;
    return Object.keys(obj).find(k => k.toLowerCase() === key.toLowerCase());
};

export const getProp = (obj, key) => {
    const actual = findKey(obj, key);
    return actual === undefined ? undefined : obj[actual];
};

export const setProp = (obj, key, value) => {
    const actual = findKey(obj, key) || key;
    obj[actual] = value;
};

export const deleteProp = (obj, key) => {
    const actual = findKey(obj, key);
    if (actual !== undefined) delete obj[actual];
};

// Sets the value when non-empty, otherwise removes the key so empty strings
// are not written into rules that never had the property.
export const setOrClearProp = (obj, key, value) => {
    if (value === undefined || value === null || value === '') {
        deleteProp(obj, key);
        return;
    }
    setProp(obj, key, value);
};

const isRule = (obj) =>
    !!obj && typeof obj === 'object' && !Array.isArray(obj) &&
    (findKey(obj, 'RuleName') !== undefined || findKey(obj, 'Expression') !== undefined);

const isWorkflow = (obj) =>
    !!obj && typeof obj === 'object' && !Array.isArray(obj) &&
    findKey(obj, 'WorkflowName') !== undefined && findKey(obj, 'Rules') !== undefined;

// Normalizes any supported ruleset shape into { root, groups } where root is the
// parsed JSON (serialized back on save) and each group is { name, rules } with
// `rules` referencing arrays inside root.
export const parseRuleset = (jsonString) => {
    const root = JSON.parse(jsonString);

    let groups;
    if (Array.isArray(root)) {
        groups = root.length > 0 && root.every(isWorkflow)
            ? root.map((w, i) => ({ name: getProp(w, 'WorkflowName') || `Workflow ${i + 1}`, rules: getProp(w, 'Rules') || [] }))
            : [{ name: 'Rules', rules: root }];
    } else if (root && typeof root === 'object' && findKey(root, 'Rules') !== undefined) {
        groups = [{ name: getProp(root, 'WorkflowName') || 'Rules', rules: getProp(root, 'Rules') || [] }];
    } else if (isRule(root)) {
        groups = [{ name: 'Rules', rules: [root] }];
    } else {
        throw new Error('Unrecognized ruleset structure');
    }

    return { root, groups };
};

const buildRuleNode = (rule, groupName, path, depth, parentArray) => ({
    id: `rule-${path}`,
    ref: rule,
    // the array that contains this rule, so edits can remove it in place
    parentArray,
    group: groupName,
    depth,
    name: getProp(rule, 'RuleName') || '(unnamed rule)',
    operator: getProp(rule, 'Operator') || '',
    expression: getProp(rule, 'Expression') || '',
    errorMessage: getProp(rule, 'ErrorMessage') || '',
    successEvent: getProp(rule, 'SuccessEvent') || '',
    enabled: getProp(rule, 'Enabled'),
    isGroup: false,
    children: (getProp(rule, 'Rules') || []).map((child, i) =>
        buildRuleNode(child, groupName, `${path}-${i}`, depth + 1, getProp(rule, 'Rules'))),
});

export const buildRuleTree = (groups) =>
    groups.map((group, g) => ({
        id: `group-${g}`,
        name: group.name,
        isGroup: true,
        depth: 0,
        children: (group.rules || []).map((rule, i) => buildRuleNode(rule, group.name, `${g}-${i}`, 0, group.rules)),
    }));

// Removes BuilderState keys an earlier builder version persisted into rule
// JSON — the builder now reconstructs its conditions from the expression, so
// nothing extra belongs in the stored ruleset.
export const stripBuilderState = (groups) => {
    const strip = (rule) => {
        deleteProp(rule, 'BuilderState');
        (getProp(rule, 'Rules') || []).forEach(strip);
    };
    groups.forEach(group => (group.rules || []).forEach(strip));
};

const nameOf = (rule) => getProp(rule, 'RuleName') || '';
const expressionOf = (rule) => getProp(rule, 'Expression') || '';

const gatherRules = (groups) => {
    const allRules = [];
    const containerOf = new Map();
    const gather = (rule, container) => {
        allRules.push(rule);
        containerOf.set(rule, container);
        (getProp(rule, 'Rules') || []).forEach(child => gather(child, getProp(rule, 'Rules')));
    };
    groups.forEach(group => (group.rules || []).forEach(rule => gather(rule, group.rules)));
    return { allRules, containerOf };
};

// rule -> the rules it references: rule B references rule A when B's expression
// contains A's RuleName as an exact quoted literal (e.g. t.name == "EPA
// 608_82157c2i_Annualizing"). Quoting prevents prefix false-matches ("EPA 608"
// inside "EPA 608_S157"). Self-references are excluded here (validateRuleset
// reports them separately).
const buildReferenceMap = (allRules) => {
    const referencesOf = new Map();
    allRules.forEach(rule => {
        const expression = expressionOf(rule);
        referencesOf.set(rule, allRules.filter(other =>
            other !== rule && nameOf(other) && expression.includes(`"${nameOf(other)}"`)
        ));
    });
    return referencesOf;
};

// Matches the tag-reference conditions the expression builder generates and the
// hand-written equivalents: t.name == "X" and t.name != "X". Unlike the
// reference map above (which can only match names of rules that exist), this
// recovers the referenced name itself, so renamed/mistyped targets surface.
const TAG_REFERENCE_PATTERN = /t\.name\s*[!=]=\s*"([^"]+)"/g;

export const extractTagReferences = (expression) =>
    [...(expression || '').matchAll(TAG_REFERENCE_PATTERN)].map(m => m[1]);

// Builds the hierarchy for the tree view from rule references. Roots are rules
// that reference no other rule; a rule referencing several parents appears under
// each of them. Rules only reachable through a reference cycle have no root to
// hang from and would silently vanish from this view, so they are surfaced as
// extra roots flagged cyclic: true (validateRuleset reports the cycle itself).
export const buildReferenceForest = (groups) => {
    const { allRules, containerOf } = gatherRules(groups);
    const referencesOf = buildReferenceMap(allRules);

    const childrenOf = (parent) => allRules.filter(rule => referencesOf.get(rule).includes(parent));

    const toNode = (rule, path, depth, ancestors) => ({
        id: `ref-${path}`,
        ref: rule,
        parentArray: containerOf.get(rule),
        depth,
        name: nameOf(rule) || '(unnamed rule)',
        operator: getProp(rule, 'Operator') || '',
        expression: expressionOf(rule),
        errorMessage: getProp(rule, 'ErrorMessage') || '',
        successEvent: getProp(rule, 'SuccessEvent') || '',
        enabled: getProp(rule, 'Enabled'),
        isGroup: false,
        children: ancestors.includes(rule)
            ? []
            : childrenOf(rule).map((child, i) => toNode(child, `${path}-${i}`, depth + 1, [...ancestors, rule])),
    });

    const roots = allRules.filter(rule => referencesOf.get(rule).length === 0);

    const reachable = new Set();
    const queue = [...roots];
    while (queue.length) {
        const rule = queue.pop();
        if (reachable.has(rule)) continue;
        reachable.add(rule);
        childrenOf(rule).forEach(child => queue.push(child));
    }
    const trapped = allRules.filter(rule => !reachable.has(rule));

    return [
        ...roots.map((rule, i) => toNode(rule, `${i}`, 0, [])),
        ...trapped.map((rule, i) => ({ ...toNode(rule, `cyclic-${i}`, 0, []), cyclic: true })),
    ];
};

// Save-time validation of rule-to-rule references. Returns { errors, maxDepth }:
// errors block SAVE RULESET (the engine keys tags by rule name, so dangling
// references silently never match, duplicates are ambiguous, and cycles never
// stabilize under the engine's converged evaluation); maxDepth is informational —
// the longest dependency chain, which converged evaluation resolves in one
// nightly run regardless of depth.
export const validateRuleset = (groups) => {
    const { allRules } = gatherRules(groups);
    const errors = [];

    const nameCounts = new Map();
    allRules.forEach(rule => {
        const name = nameOf(rule);
        if (name) nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
    });
    [...nameCounts].filter(([, count]) => count > 1).forEach(([name, count]) =>
        errors.push(`Duplicate rule name "${name}" (${count} rules) — tags and references are keyed by rule name, so duplicates are ambiguous.`));

    const knownNames = new Set(nameCounts.keys());
    allRules.forEach(rule => {
        const ruleName = nameOf(rule) || '(unnamed rule)';
        extractTagReferences(expressionOf(rule)).forEach(target => {
            if (target === nameOf(rule)) {
                errors.push(`Rule "${ruleName}" references its own tag — a self-reference can never stabilize.`);
            } else if (!knownNames.has(target)) {
                errors.push(`Rule "${ruleName}" references tag "${target}", which matches no rule in this ruleset — renamed or mistyped? The engine writes tags under rule names, so this condition can never match a current tag.`);
            }
        });
    });

    const referencesOf = buildReferenceMap(allRules);

    // DFS coloring over rule -> referenced-rule edges; each cycle is reported once.
    const WHITE = 0, GRAY = 1, BLACK = 2;
    const color = new Map(allRules.map(rule => [rule, WHITE]));
    const stack = [];
    const visit = (rule) => {
        color.set(rule, GRAY);
        stack.push(rule);
        referencesOf.get(rule).forEach(dep => {
            if (color.get(dep) === GRAY) {
                const path = [...stack.slice(stack.indexOf(dep)), dep]
                    .map(r => `"${nameOf(r) || '(unnamed rule)'}"`).join(' → ');
                errors.push(`Rule reference cycle: ${path} — cyclic rules never stabilize and cannot converge.`);
            } else if (color.get(dep) === WHITE) {
                visit(dep);
            }
        });
        stack.pop();
        color.set(rule, BLACK);
    };
    allRules.forEach(rule => {
        if (color.get(rule) === WHITE) visit(rule);
    });

    // Longest dependency chain (0 = no references). Back-edges are skipped —
    // cycles are already reported above.
    const depthMemo = new Map();
    const onPath = new Set();
    const depthOf = (rule) => {
        if (depthMemo.has(rule)) return depthMemo.get(rule);
        if (onPath.has(rule)) return 0;
        onPath.add(rule);
        const deps = referencesOf.get(rule);
        const depth = deps.length === 0 ? 0 : 1 + Math.max(...deps.map(depthOf));
        onPath.delete(rule);
        depthMemo.set(rule, depth);
        return depth;
    };
    const maxDepth = allRules.length === 0 ? 0 : Math.max(...allRules.map(depthOf));

    return { errors, maxDepth };
};

// Flattens the tree into rows for the tabular view; nesting is conveyed by
// each row's depth (indented in the Rule column).
export const flattenRuleTree = (tree) => {
    const rows = [];
    const walk = (node) => {
        if (!node.isGroup) {
            rows.push(node);
        }
        node.children.forEach(walk);
    };
    tree.forEach(walk);
    return rows;
};
